import { AudioFile, ServiceConfiguration } from '../types';
import { apiClient } from './api';
import { extractMediaDuration } from '../utils/mediaUtils';

interface UploadAudioFileRequest {
  file: File;
  folderId?: string;
}

interface AudioFilesResponse {
  data: AudioFile[];
}

interface AudioFileResponse {
  data: AudioFile;
}

class AudioFilesService {
  private baseUrl = '/audio_files';

  async getAudioFiles(folderId?: string | null): Promise<AudioFilesResponse> {
    let params = '';
    if (folderId !== undefined) {
      // If folderId is explicitly provided (including null for Common folder)
      params = `?folder_id=${folderId || ''}`;
    }
    // If folderId is undefined, no params means "get all files"

    const response = await apiClient.get<AudioFilesResponse>(
      `${this.baseUrl}${params}`
    );
    return response.data;
  }

  async getAudioFile(id: string): Promise<AudioFile> {
    const response = await apiClient.get<AudioFileResponse>(
      `${this.baseUrl}/${id}`
    );
    return response.data.data;
  }

  async uploadAudioFile(data: UploadAudioFileRequest): Promise<AudioFile> {
    // Step 1: Extract duration from media file (before upload)
    const durationSeconds = await extractMediaDuration(data.file);

    // Step 2: Get presigned upload URL
    const uploadUrlResponse = await apiClient.post<{
      upload_url: string;
      key: string;
      expires_in: number;
    }>(`${this.baseUrl}/upload-url`, {
      filename: data.file.name,
      content_type: data.file.type,
      file_size: data.file.size,
    });

    const { upload_url, key } = uploadUrlResponse.data;

    // Step 3: Upload file directly to S3
    const s3Response = await fetch(upload_url, {
      method: 'PUT',
      body: data.file,
    });

    if (!s3Response.ok) {
      const errorText = await s3Response.text();
      throw new Error(`S3 upload failed: ${s3Response.status} ${errorText}`);
    }

    // Step 4: Confirm upload with backend (including duration)
    const confirmResponse = await apiClient.post<AudioFileResponse>(
      `${this.baseUrl}/confirm-upload`,
      {
        filename: data.file.name,
        key: key,
        file_size: data.file.size,
        content_type: data.file.type,
        folder_id: data.folderId,
        duration_seconds: durationSeconds,
      }
    );

    return confirmResponse.data.data;
  }

  async deleteAudioFile(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async moveAudioFile(
    id: string,
    folderId?: string | null
  ): Promise<AudioFile> {
    const response = await apiClient.put<AudioFileResponse>(
      `${this.baseUrl}/${id}`,
      {
        audio_file: { folder_id: folderId },
      }
    );
    return response.data.data;
  }

  async updateAudioFile(
    id: string,
    data: { name?: string; comment?: string }
  ): Promise<AudioFile> {
    const response = await apiClient.put<AudioFileResponse>(
      `${this.baseUrl}/${id}`,
      {
        audio_file: data,
      }
    );
    return response.data.data;
  }

  async getDownloadUrl(
    id: string
  ): Promise<{ download_url: string; expires_in: number }> {
    const response = await apiClient.get<{
      download_url: string;
      expires_in: number;
    }>(`${this.baseUrl}/${id}/download`);
    return response.data;
  }

  // Validate file format and size to match backend requirements
  validateFile(
    file: File,
    serviceConfig?: ServiceConfiguration
  ): { isValid: boolean; error?: string } {
    // If service configuration is provided, use it for validation
    if (serviceConfig) {
      return this.validateFileWithServiceConfig(file, serviceConfig);
    }

    // Fall back to legacy validation if no service config provided
    // Backend supported MIME types from constants.rb
    const allowedMimeTypes = [
      'audio/3ga',
      'audio/3gpp',
      'audio/3gpp2',
      'audio/8svx',
      'audio/aac',
      'audio/aiff',
      'audio/x-aiff',
      'audio/alac',
      'audio/amr',
      'audio/x-ape',
      'audio/basic',
      'audio/dss',
      'audio/flac',
      'audio/x-flv',
      'audio/mp4',
      'audio/m4a',
      'audio/x-m4a',
      'audio/m4b',
      'audio/m4p',
      'audio/m4r',
      'audio/mpeg',
      'audio/mp3',
      'audio/mpga',
      'audio/ogg',
      'audio/oga',
      'audio/opus',
      'audio/qcelp',
      'audio/tta',
      'audio/voc',
      'audio/wav',
      'audio/x-wav',
      'audio/webm',
      'audio/x-ms-wma',
      'audio/wavpack',
      'video/3gpp',
      'video/3gpp2',
      'video/webm',
      'video/mp2t',
      'video/quicktime',
      'video/mp2',
      'video/mp4',
      'video/mpeg',
      'video/ogg',
      'video/x-m4v',
      'video/x-msvideo',
      'video/mxf',
    ];

    // Backend allowed extensions from audio_files_controller.rb
    const allowedExtensions = [
      '.3ga',
      '.3gp',
      '.3g2',
      '.8svx',
      '.aac',
      '.aif',
      '.aiff',
      '.alac',
      '.amr',
      '.ape',
      '.au',
      '.dss',
      '.flac',
      '.flv',
      '.m4a',
      '.m4b',
      '.m4p',
      '.m4r',
      '.mp3',
      '.mpga',
      '.ogg',
      '.oga',
      '.mogg',
      '.opus',
      '.qcp',
      '.tta',
      '.voc',
      '.wav',
      '.wma',
      '.wv',
      '.webm',
      '.weba',
      '.mts',
      '.m2ts',
      '.ts',
      '.mov',
      '.mp2',
      '.mp4',
      '.m4v',
      '.mpeg',
      '.ogv',
      '.avi',
      '.mxf',
    ];

    // Backend file size limit: 500MB
    const maxSizeInBytes = 500 * 1024 * 1024; // 500MB

    // Check MIME type
    if (!allowedMimeTypes.includes(file.type)) {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

      // Sometimes browsers don't set MIME type correctly, so also check extension
      if (!allowedExtensions.includes(fileExtension)) {
        return {
          isValid: false,
          error:
            'File format not supported. Please upload MP3, WAV, M4A, MP4, FLAC, AAC, OGG, or WMA files.',
        };
      }
    }

    // Check file extension as additional validation
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        error:
          'File extension not supported. Please upload files with: .mp3, .wav, .m4a, .mp4, .flac, .aac, .ogg, .wma extensions.',
      };
    }

    // Check file size (500MB limit)
    if (file.size > maxSizeInBytes) {
      return {
        isValid: false,
        error: 'File size exceeds 500MB limit.',
      };
    }

    // Check filename length (backend limit: 255 characters)
    if (file.name.length > 255) {
      return {
        isValid: false,
        error: 'Filename too long. Maximum 255 characters allowed.',
      };
    }

    return { isValid: true };
  }

  // Validate file using service configuration
  private validateFileWithServiceConfig(
    file: File,
    serviceConfig: ServiceConfiguration
  ): { isValid: boolean; error?: string } {
    // Check file size using service config
    if (file.size > serviceConfig.file_constraints.max_file_size) {
      const maxSizeMB = Math.round(
        serviceConfig.file_constraints.max_file_size / (1024 * 1024)
      );
      return {
        isValid: false,
        error: `File size exceeds maximum allowed size of ${maxSizeMB}MB`,
      };
    }

    // Check file format using service config
    const fileType = file.type;
    if (!serviceConfig.file_constraints.supported_formats.includes(fileType)) {
      // Extract file extension for additional check
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

      // Create a basic extension to MIME type mapping for fallback
      const extensionToMimeMap: { [key: string]: string[] } = {
        '.mp3': ['audio/mp3', 'audio/mpeg'],
        '.wav': ['audio/wav', 'audio/x-wav'],
        '.m4a': ['audio/m4a', 'audio/x-m4a', 'audio/mp4'],
        '.mp4': ['video/mp4', 'audio/mp4'],
        '.flac': ['audio/flac'],
        '.aac': ['audio/aac'],
        '.ogg': ['audio/ogg', 'audio/oga'],
        '.wma': ['audio/x-ms-wma'],
      };

      // Check if the extension maps to any supported MIME type
      const possibleMimeTypes = extensionToMimeMap[fileExtension] || [];
      const isExtensionSupported = possibleMimeTypes.some((mimeType) =>
        serviceConfig.file_constraints.supported_formats.includes(mimeType)
      );

      if (!isExtensionSupported) {
        return {
          isValid: false,
          error:
            process.env.REACT_APP_TRANSCRIBING_SERVICE_SELECTION === 'true'
              ? `File format "${fileType || fileExtension}" is not supported by the active transcription service (${serviceConfig.transcription.active_service_name}). Supported formats: ${serviceConfig.file_constraints.supported_formats.join(', ')}`
              : `File format "${fileType || fileExtension}" is not supported. Supported formats: ${serviceConfig.file_constraints.supported_formats.join(', ')}`,
        };
      }
    }

    // Check filename length (backend limit: 255 characters)
    if (file.name.length > 255) {
      return {
        isValid: false,
        error: 'Filename too long. Maximum 255 characters allowed.',
      };
    }

    return { isValid: true };
  }
}

export const audioFilesService = new AudioFilesService();
